import React from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useMutation } from '@tanstack/react-query'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import { allCountries } from 'constants/supportedCountriesList'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        label={label}
        inputFormat='dd/MM/yyyy'
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  name: '',
  trafficPolicyId: 0,
  supportedCountries: '',
  preferredImsiId: 0,
  voiceUsageAllowanceInSeconds: 0,
  dataUsageAllowanceInBytes: 0,
  smsUsageAllowanceInNums: 0,
  timeAllowanceInSeconds: 0,
  activationTimeAllowanceInSeconds: 94608000, // 3 years
  activationType: 'PDP',
  dateEarliestActivation: 1702500000000,
  dateEarliestAvailable: 1702500000000,
  dateLatestAvailable: 2067875200000,
  notes: '',
  dataUsageAllowanceType: 'LIMITED',
  voiceUsageAllowanceType: 'LIMITED',
  smsUsageAllowanceType: 'LIMITED'
}

const NewPackageSchema = Yup.object().shape({
  name: Yup.string().required('Package name is required'),
  trafficPolicyId: Yup.string()
    .matches(/^[0-9]+$/, 'Traffic Policy ID must be only digits')
    .required('Traffic Policy ID is required'),
  preferredImsiId: Yup.number().required('Preferred IMSI ID is required'),
  voiceUsageAllowanceInSeconds: Yup.number()
    .min(0, 'Input cannot be negative')
    .required('Input is required'),
  dataUsageAllowanceInBytes: Yup.number()
    .min(0, 'Input cannot be negative')
    .required('Input is required')
})

const CenteredBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '80%'
})
const CenteredGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center'
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const NewPackageForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const faberToken = OrbitSession.get('faberToken')

  const isValidCountryCode = (code: string) => {
    return code.length === 3 && Object.values(allCountries).includes(code)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  const { mutate: createPackage } = useMutation(CatalogAPI.createTelnaPackage, {
    onSuccess: () => {
      toast.success('Package created successfully!')
      handleClose()
    },
    onError: () => {
      toast.error('Failed to create package!')
    }
  })

  const handleFormSubmit = (values: NewPackageFormikProps) => {
    const supportedCountries = values.supportedCountries
      .split(',')
      .map((code) => code.trim())
      .filter((code) => {
        return isValidCountryCode(code)
      })

    if (
      supportedCountries.length !== values.supportedCountries.split(',').length
    ) {
      toast.error('Invalid countries')
      return
    }

    const secondsInDay = 86400

    const allValues = {
      name: values.name,
      trafficPolicyId: values.trafficPolicyId,
      supportedCountries: supportedCountries,
      preferredImsiId: values.preferredImsiId,
      voiceUsageAllowanceInSeconds: values.voiceUsageAllowanceInSeconds,
      dataUsageAllowanceInBytes: values.dataUsageAllowanceInBytes * 1073741824, // Convert GB to bytes
      smsUsageAllowanceInNums: values.smsUsageAllowanceInNums,
      timeAllowanceInSeconds: values.timeAllowanceInSeconds * secondsInDay, // Convert days to seconds
      activationTimeAllowanceInSeconds: values.timeAllowanceInSeconds > (90 * secondsInDay) ? (365 * secondsInDay) : (90 * secondsInDay),
      activationType: values.activationType,
      dateEarliestActivation: values.dateEarliestActivation,
      dateEarliestAvailable: values.dateEarliestAvailable,
      dateLatestAvailable: values.dateLatestAvailable,
      notes: values.notes,
      dataUsageAllowanceType: values.dataUsageAllowanceType,
      voiceUsageAllowanceType: values.voiceUsageAllowanceType,
      smsUsageAllowanceType: values.smsUsageAllowanceType,
      faberToken: faberToken
    }
    createPackage(allValues)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewPackageSchema}
    >
      {({
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        submitForm
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            {/* PACKAGE NAME */}
            <Grid item xs={3}>
              <NormalText isBold>Package Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='name'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'package-name'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.name && touched.name)}
                  helperText={errors.name && touched.name ? errors.name : null}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Supported Countries</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'supported-countries'
                  }}
                  name='supportedCountries'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  multiline
                  variant='outlined'
                  error={
                    !!(errors.supportedCountries && touched.supportedCountries)
                  }
                  helperText={
                    errors.supportedCountries && touched.supportedCountries
                      ? errors.supportedCountries
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Data Usage Allowance (GB)</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='dataUsageAllowanceInBytes'
                  type='number'
                  inputProps={{
                    min: 0
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={
                    !!(
                      errors.dataUsageAllowanceInBytes &&
                      touched.dataUsageAllowanceInBytes
                    )
                  }
                  helperText={
                    errors.dataUsageAllowanceInBytes &&
                      touched.dataUsageAllowanceInBytes
                      ? errors.dataUsageAllowanceInBytes
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Validity (Days)</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='timeAllowanceInSeconds'
                  type='number'
                  inputProps={{
                    min: 0
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={
                    !!(
                      errors.timeAllowanceInSeconds &&
                      touched.timeAllowanceInSeconds
                    )
                  }
                  helperText={
                    errors.timeAllowanceInSeconds &&
                      touched.timeAllowanceInSeconds
                      ? errors.timeAllowanceInSeconds
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Activation Type</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='activationType'
                defaultValue='PDP'
              >
                <MenuItem value='API'>API</MenuItem>
                <MenuItem value='PDP'>PDP (Choose this if not sure)</MenuItem>
                <MenuItem value='LU'>LU</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Notes</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'notes'
                  }}
                  name='notes'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  multiline
                  variant='outlined'
                  error={!!(errors.notes && touched.notes)}
                  helperText={
                    errors.notes && touched.notes ? errors.notes : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Create package</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewPackageFormikProps {
  name: string
  trafficPolicyId: number
  supportedCountries: string
  preferredImsiId: number
  voiceUsageAllowanceInSeconds: number
  dataUsageAllowanceInBytes: number
  smsUsageAllowanceInNums: number
  timeAllowanceInSeconds: number
  activationTimeAllowanceInSeconds: number
  activationType: string
  dateEarliestActivation: number
  dateEarliestAvailable: number
  dateLatestAvailable: number
  notes: string
  dataUsageAllowanceType: string
  voiceUsageAllowanceType: string
  smsUsageAllowanceType: string
}
